import React, { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { Upload, X, Check, ZoomIn, ZoomOut, Crop } from 'lucide-react';
import { useDropzone } from 'react-dropzone';

interface Point {
    x: number;
    y: number;
}

interface Area {
    width: number;
    height: number;
    x: number;
    y: number;
}

interface ImageUploadWithCropProps {
    label: string;
    initialImage?: string;
    onImageChange: (blob: Blob | null, base64Preview: string | null) => void;
    aspectRatio?: number;
    /** The stored image was cropped at a DIFFERENT aspect ratio than the one
     *  now in force (the operator changed the header logo shape). Surfaces a
     *  visible "Re-crop for this shape" button — the tiny corner icon is there
     *  either way, but nobody finds it. */
    recropHint?: boolean;
}

const ImageUploadWithCrop: React.FC<ImageUploadWithCropProps> = ({
    label,
    initialImage,
    onImageChange,
    aspectRatio = 1,
    recropHint = false,
}) => {
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [preview, setPreview] = useState<string | null>(initialImage || null);
    const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
    const [showCropper, setShowCropper] = useState(false);
    const [cropError, setCropError] = useState<string | null>(null);

    // Sync preview with initialImage prop changes
    useEffect(() => {
        setPreview(initialImage || null);
    }, [initialImage]);

    const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        if (acceptedFiles && acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            const reader = new FileReader();
            reader.addEventListener('load', async () => {
                const src = reader.result as string;
                setImageSrc(src);

                // Set default crop to center based on aspect ratio
                // This ensures we get *some* image blob automatically
                // even before they hit Apply Crop

                // Show cropper to allow fine tuning
                setShowCropper(true);
            });
            reader.readAsDataURL(file);
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'image/*': []
        },
        multiple: false
    });

    const createImage = (url: string): Promise<HTMLImageElement> =>
        new Promise((resolve, reject) => {
            const image = new Image();
            image.addEventListener('load', () => resolve(image));
            image.addEventListener('error', (error) => reject(error));
            image.setAttribute('crossOrigin', 'anonymous');
            // A stored logo is a remote S3 URL served with a one-year
            // Cache-Control, and the thumbnail above already fetched it WITHOUT
            // `crossOrigin`. A cached no-CORS response cannot serve a CORS
            // request, so the load fails or the canvas read taints. A unique
            // query string forces a fresh, CORS-flavoured fetch. These S3 URLs
            // are public, never presigned, so an extra param is harmless.
            image.src = /^https?:/i.test(url)
                ? `${url}${url.includes('?') ? '&' : '?'}recrop=${Date.now()}`
                : url;
        });

    const getCroppedImg = async (
        imageSrc: string,
        pixelCrop: Area
    ): Promise<{ blob: Blob; base64: string }> => {
        const image = await createImage(imageSrc);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            return Promise.reject(new Error('Failed to get canvas context'));
        }

        // Set max dimensions for the output image (e.g., 512px for icons/logos)
        // This significantly reduces file size while maintaining sufficient quality for UI
        const maxSize = 512;
        let width = pixelCrop.width;
        let height = pixelCrop.height;

        // Scale down if dimensions exceed maxSize
        if (width > maxSize || height > maxSize) {
            const ratio = Math.min(maxSize / width, maxSize / height);
            width *= ratio;
            height *= ratio;
        }

        canvas.width = width;
        canvas.height = height;

        // Use high quality image smoothing
        ctx.imageSmoothingQuality = 'high';

        // When zoomed out, the pixelCrop x,y might be negative
        // which means the source image should be drawn at a positive offset
        // inside the new canvas, and the crop width/height are larger than the image.

        // Calculate the actual source crop area (clamped to image bounds)
        const srcX = Math.max(0, pixelCrop.x);
        const srcY = Math.max(0, pixelCrop.y);
        const srcWidth = Math.min(image.width - srcX, pixelCrop.width + Math.min(0, pixelCrop.x));
        const srcHeight = Math.min(image.height - srcY, pixelCrop.height + Math.min(0, pixelCrop.y));

        // Calculate where to draw this piece on the destination canvas
        // (scaling based on how much the final canvas was resized to respect maxSize)
        const scaleX = width / pixelCrop.width;
        const scaleY = height / pixelCrop.height;

        const destX = Math.max(0, -pixelCrop.x) * scaleX;
        const destY = Math.max(0, -pixelCrop.y) * scaleY;
        const destWidth = srcWidth * scaleX;
        const destHeight = srcHeight * scaleY;

        // Ensure we don't try to draw a zero-dimension image
        if (srcWidth > 0 && srcHeight > 0) {
            ctx.drawImage(
                image,
                srcX,
                srcY,
                srcWidth,
                srcHeight,
                destX,
                destY,
                destWidth,
                destHeight
            );
        }

        // Return both Blob and DataURL
        return new Promise((resolve, reject) => {
            // Use quality 0.9 for better compression if compatible formats used, but PNG is lossless
            canvas.toBlob((blob) => {
                if (blob) {
                    const base64 = canvas.toDataURL('image/png');
                    resolve({ blob, base64 });
                } else {
                    reject(new Error('Failed to create blob'));
                }
            }, 'image/png');
        });
    };

    const handleSave = async () => {
        if (imageSrc && croppedAreaPixels) {
            try {
                // Get cropped image
                const { blob, base64 } = await getCroppedImg(imageSrc, croppedAreaPixels);

                setPreview(base64);
                onImageChange(blob, base64);
                setCropError(null);
                setShowCropper(false);
            } catch (e: any) {
                // Previously this only hit the console: "Apply Crop" did
                // nothing at all and the operator had no idea why. Reading a
                // remote image onto a canvas can still fail (CORS, a decode
                // error), so say so where they can see it.
                console.error('Crop error:', e);
                setCropError("Couldn't process this image. Please re-upload the logo file.");
            }
        }
    };

    const handleRemove = () => {
        setPreview(null);
        setImageSrc(null);
        onImageChange(null, null);
    };

    return (
        <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {label}
            </label>

            {showCropper && imageSrc ? (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-xl overflow-hidden">
                        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                            <h3 className="font-semibold text-gray-900 dark:text-white">Crop Image</h3>
                            <button onClick={() => setShowCropper(false)} className="text-gray-500 hover:text-gray-700">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="relative h-64 sm:h-80 w-full bg-gray-900">
                            <Cropper
                                image={imageSrc}
                                crop={crop}
                                zoom={zoom}
                                aspect={aspectRatio}
                                onCropChange={setCrop}
                                onCropComplete={onCropComplete}
                                onZoomChange={setZoom}
                                restrictPosition={false}
                                minZoom={0.1}
                                zoomSpeed={0.5}
                            />
                        </div>

                        <div className="p-4 space-y-4">
                            <div className="flex items-center space-x-2">
                                <ZoomOut className="w-4 h-4 text-gray-500" />
                                <input
                                    type="range"
                                    value={zoom}
                                    min={0.1}
                                    max={3}
                                    step={0.1}
                                    aria-labelledby="Zoom"
                                    onChange={(e) => setZoom(Number(e.target.value))}
                                    className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                                />
                                <ZoomIn className="w-4 h-4 text-gray-500" />
                            </div>

                            {cropError && (
                                <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                                    {cropError}
                                </p>
                            )}

                            <div className="flex justify-end space-x-2">
                                <button
                                    onClick={() => setShowCropper(false)}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSave}
                                    className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 flex items-center"
                                >
                                    <Check className="w-4 h-4 mr-1" />
                                    Apply Crop
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : preview ? (
                <>
                <div className="relative inline-block">
                    <img
                        src={preview}
                        alt="Preview"
                        className="w-24 h-24 object-cover rounded-lg border border-gray-200 dark:border-gray-700"
                    />
                    <button
                        onClick={handleRemove}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600 transition-colors"
                    >
                        <X className="w-3 h-3" />
                    </button>
                    <button
                        onClick={() => {
                            setCropError(null);
                            setImageSrc(preview);
                            setShowCropper(true);
                        }}
                        className="absolute bottom-0 right-0 bg-blue-500 text-white rounded-full p-1.5 shadow-md hover:bg-blue-600 transition-colors transform translate-x-1/4 translate-y-1/4"
                        title="Edit Crop"
                    >
                        <ZoomIn className="w-3 h-3" />
                    </button>
                </div>
                {recropHint && (
                    <button
                        type="button"
                        onClick={() => {
                            setCropError(null);
                            setImageSrc(preview);
                            setShowCropper(true);
                        }}
                        className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors"
                    >
                        <Crop className="w-3.5 h-3.5" />
                        <span>Re-crop for this shape</span>
                    </button>
                )}
                </>
            ) : (
                <div
                    {...getRootProps()}
                    className={`border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer transition-colors ${isDragActive
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-300 dark:border-gray-600 hover:border-primary'
                        }`}
                >
                    <input {...getInputProps()} />
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                        {isDragActive ? 'Drop image here' : 'Click or drop image'}
                    </p>
                </div>
            )}
        </div>
    );
};

export default ImageUploadWithCrop;
